import type { ColumnDef, EnemyDef, LevelDef, PickupDef, SegmentDef } from '../../types';

// OP-201 Puerto de Buenaventura (GDD §10.4, §10.2, issue #107): the port in the afternoon, written by hand with the
// format of §15.6. 600 u at 5.5 u/s (world 2, level 1): takeoff, warm-up (20 %), tension (30 %) up to CP-201-A at
// x 300, respite (10 %), peak (40 %) and the clandestine airstrip. The gap columns are container stacks every 8.5 u
// with the 3.7 u gap of world 2 (§5.4), the gap centre never moving more than 3.0 u. Hazards: the port cranes
// (EN-21), that stand in the place of a column with two passages (the beam at the height of the neighbouring gaps, so
// both passages are within reach), and the seagull flocks (EN-23, announced with INTERCEPT, high above the gaps).
// The first crane comes alone after the warm-up; from x 350 to 480 Chepe Santacroche takes over the cranes from his
// cabin (the event `chepe-cranes`: five cranes that all start in the same position and turn on the same beat). No
// antenna (EN-22: OP-202 teaches it). An armor before the checkpoint and a bribe in the respite. All x are local.

const column = (x: number, gapY: number): ColumnDef => ({ x, gapY, gapH: 3.7, skin: 'w2-containers' });
const dollar = (x: number, y: number): PickupDef => ({ x, y, type: 'dollar' });
const armor = (x: number, y: number): PickupDef => ({ x, y, type: 'armor' });
const bribe = (x: number, y: number): PickupDef => ({ x, y, type: 'bribe' });
/** A port crane in the place of a column: its beam at (x, y); `startBlocked` 0 blocks the high passage, 1 the low one. */
const crane = (x: number, y: number, startBlocked: number): EnemyDef => ({ x, y, type: 'EN-21', params: { startBlocked, phaseOffset: 0 } });
/** A flock of three seagulls in a V (the leader is at x, y). */
const gull = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-23' });

const takeoff: SegmentDef = { id: 'w2-port-takeoff-01', kind: 'takeoff', length: 40, columns: [], enemies: [], pickups: [] };

const warmup01: SegmentDef = {
  id: 'w2-port-warmup-01',
  kind: 'pattern',
  phase: 'warmup',
  length: 50,
  columns: [
    column(20.0, 6.0),
    column(28.5, 6.5),
    column(37.0, 6.0),
    column(45.5, 5.5),
  ],
  enemies: [],
  pickups: [
    dollar(23.25, 6.0),
    dollar(24.25, 6.3),
    dollar(25.25, 6.0),
    dollar(40.25, 5.5),
    dollar(41.25, 5.8),
    dollar(42.25, 5.5),
  ],
};

const warmup02: SegmentDef = {
  id: 'w2-port-warmup-02',
  kind: 'pattern',
  phase: 'warmup',
  length: 50,
  columns: [
    column(4.0, 6.0),
    column(12.5, 6.5),
    column(21.0, 6.0),
    column(29.5, 6.5),
    column(38.0, 6.0),
    column(46.5, 5.5),
  ],
  enemies: [],
  pickups: [
    dollar(15.75, 6.0),
    dollar(16.75, 6.3),
    dollar(17.75, 6.0),
    dollar(32.75, 6.0),
    dollar(33.75, 6.3),
    dollar(34.75, 6.0),
  ],
};

const tension01: SegmentDef = {
  id: 'w2-port-tension-01',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [
    column(5.0, 7.0),
    column(13.5, 5.5),
    column(22.0, 7.5),
    column(30.5, 6.0),
    column(47.5, 6.0),
  ],
  enemies: [crane(39.0, 6.0, 0)],
  pickups: [
    dollar(8.25, 6.0),
    dollar(9.25, 6.3),
    dollar(10.25, 6.0),
    dollar(25.25, 6.5),
    dollar(26.25, 6.8),
    dollar(27.25, 6.5),
  ],
};

const tension02: SegmentDef = {
  id: 'w2-port-tension-02',
  kind: 'pattern',
  phase: 'tension',
  length: 50,
  columns: [
    column(6.0, 7.0),
    column(14.5, 7.0),
    column(23.0, 5.5),
    column(31.5, 7.5),
    column(40.0, 6.0),
    column(48.5, 8.0),
  ],
  enemies: [gull(21.5, 10.8)],
  pickups: [
    dollar(17.75, 6.0),
    dollar(18.75, 6.3),
    dollar(19.75, 6.0),
    dollar(34.75, 6.5),
    dollar(35.75, 6.8),
    dollar(36.75, 6.5),
    armor(27.25, 6.5),
  ],
};

const tension03: SegmentDef = {
  id: 'w2-port-tension-03',
  kind: 'pattern',
  phase: 'tension',
  length: 60,
  columns: [
    column(7.0, 5.5),
    column(15.5, 7.0),
    column(24.0, 7.0),
    column(32.5, 6.0),
    column(49.5, 6.0),
    column(58.0, 8.0),
  ],
  enemies: [crane(41.0, 6.0, 0), gull(25.0, 10.8)],
  pickups: [
    dollar(18.75, 6.75),
    dollar(19.75, 7.05),
    dollar(20.75, 6.75),
    dollar(52.75, 6.75),
    dollar(53.75, 7.05),
    dollar(54.75, 6.75),
  ],
};

const respite01: SegmentDef = {
  id: 'w2-port-respite-01',
  kind: 'pattern',
  phase: 'respite',
  length: 50,
  columns: [
    column(6.5, 6.0),
    column(15.0, 6.5),
    column(23.5, 6.0),
    column(32.0, 6.5),
    column(40.5, 6.0),
    column(49.0, 6.5),
  ],
  enemies: [gull(40.0, 10.8)],
  pickups: [
    dollar(18.25, 6.0),
    dollar(19.25, 6.3),
    dollar(20.25, 6.0),
    dollar(35.25, 6.0),
    dollar(36.25, 6.3),
    dollar(37.25, 6.0),
    bribe(10.75, 6.25),
  ],
};

const peak01: SegmentDef = {
  id: 'w2-port-peak-01',
  kind: 'pattern',
  phase: 'peak',
  length: 65,
  columns: [
    column(7.5, 6.0),
    column(24.5, 6.0),
    column(33.0, 6.0),
    column(50.0, 6.0),
    column(58.5, 6.0),
  ],
  enemies: [crane(16.0, 6.0, 0), crane(41.5, 6.0, 0)],
  pickups: [
    dollar(27.75, 5.75),
    dollar(28.75, 6.05),
    dollar(29.75, 5.75),
    dollar(53.25, 5.75),
    dollar(54.25, 6.05),
    dollar(55.25, 5.75),
  ],
};

const peak02: SegmentDef = {
  id: 'w2-port-peak-02',
  kind: 'pattern',
  phase: 'peak',
  length: 65,
  columns: [
    column(10.5, 6.0),
    column(19.0, 6.0),
    column(36.0, 6.0),
    column(44.5, 6.0),
    column(61.5, 6.0),
  ],
  enemies: [crane(2.0, 6.0, 0), crane(27.5, 6.0, 0), crane(53.0, 6.0, 0)],
  pickups: [
    dollar(13.75, 5.75),
    dollar(14.75, 6.05),
    dollar(15.75, 5.75),
    dollar(39.25, 5.75),
    dollar(40.25, 6.05),
    dollar(41.25, 5.75),
  ],
};

const peak03: SegmentDef = {
  id: 'w2-port-peak-03',
  kind: 'pattern',
  phase: 'peak',
  length: 60,
  columns: [
    column(5.0, 8.0),
    column(13.5, 8.5),
    column(22.0, 6.0),
    column(30.5, 9.0),
    column(39.0, 6.5),
    column(47.5, 4.5),
  ],
  enemies: [gull(40.5, 10.8)],
  pickups: [
    dollar(16.75, 7.0),
    dollar(17.75, 7.3),
    dollar(18.75, 7.0),
    dollar(33.75, 7.5),
    dollar(34.75, 7.8),
    dollar(35.75, 7.5),
  ],
};

const airstrip: SegmentDef = { id: 'w2-port-airstrip-01', kind: 'airstrip', length: 60, columns: [], enemies: [], pickups: [] };

export const OP_201_SEGMENTS: readonly SegmentDef[] = [takeoff, warmup01, warmup02, tension01, tension02, tension03, respite01, peak01, peak02, peak03, airstrip];

export const OP_201: LevelDef = {
  id: '2-1',
  op: 'OP-201',
  name: 'Puerto de Buenaventura',
  world: 2,
  scrollSpeed: 5.5,
  timeOfDay: 'afternoon',
  segments: OP_201_SEGMENTS.map((segment) => segment.id),
  checkpoints: [{ id: 'CP-201-A', x: 300 }],
  ending: 'airstrip',
  // Chepe Santacroche controls the cranes of this stretch from his cabin (GDD §10.4, #107): data only; the runner
  // that makes them turn on the same beat and shows the radio line comes later (@gdd-pending).
  events: [{ id: 'chepe-cranes', fromX: 350, toX: 480 }],
};
